import React, { useState } from 'react';
import { FiCpu, FiMapPin, FiAlertOctagon, FiGlobe, FiInfo } from 'react-icons/fi';
import { clsx } from 'clsx';
import SafeIcon from '../common/SafeIcon';

const RecommendationEngine = ({ stats, aiMode, geoMode }) => {
  const [showGlossary, setShowGlossary] = useState(false);
  const { ping, stability } = stats;
  const recommendations = [];

  // Intelligence Logic for Real-time SEO Insights
  if (aiMode) {
    if (stability > 90 && ping < 50) {
      recommendations.push({
        type: 'success',
        icon: FiCpu,
        text: 'AI Pipeline Optimized: High-fidelity connection detected for LLM context processing.',
        label: 'AI READY'
      });
    } else if (stability < 70) {
      recommendations.push({
        type: 'danger',
        icon: FiAlertOctagon,
        text: 'AI Latency Risk: Unstable connection may cause token streaming timeouts.',
        label: 'AI WARNING'
      });
    }
  }

  if (geoMode) {
    if (ping < 30) {
      recommendations.push({
        type: 'success',
        icon: FiMapPin,
        text: 'Local Edge Precision: Optimal for verifying geo-targeted SERPs and local Map Pack rankings.',
        label: 'GEO SYNCED'
      });
    } else {
      recommendations.push({
        type: 'warning',
        icon: FiGlobe,
        text: 'Geo-Location Drift: Latency exceeds 100ms. Results may show regional variance.',
        label: 'GEO DRIFT'
      });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted">Intelligence Core</h3>
        <button 
          onClick={() => setShowGlossary(!showGlossary)}
          className="flex items-center gap-1 text-[9px] font-bold text-primary hover:text-primary/80 transition-colors uppercase tracking-widest"
        >
          <SafeIcon icon={FiInfo} size={10} /> {showGlossary ? 'Hide Details' : 'What is this?'}
        </button>
      </div>
      
      {showGlossary && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4 animate-in fade-in zoom-in-95 duration-300">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-primary">
              <SafeIcon icon={FiCpu} size={14} />
              <h4 className="text-[11px] font-black uppercase tracking-wider">AI SEO Intelligence</h4>
            </div>
            <p className="text-[11px] text-muted leading-relaxed">
              Monitors sub-100ms stability for fluid AI token generation. High jitter causes "stuttering" in streaming responses.
            </p>
          </div>
          <div className="h-px bg-white/5" />
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-warning">
              <SafeIcon icon={FiMapPin} size={14} />
              <h4 className="text-[11px] font-black uppercase tracking-wider">Geo SEO Intelligence</h4>
            </div>
            <p className="text-[11px] text-muted leading-relaxed">
              Validates edge-node proximity. Latency {'>'} 150ms correlates with IP routing drift, affecting local SERP accuracy.
            </p>
          </div>
        </div>
      )}

      {/* Real-time Recommendations */}
      <div className="space-y-3">
        {recommendations.length > 0 ? recommendations.map((rec, i) => (
          <div key={i} className={clsx(
            "p-3 rounded-xl border text-sm transition-all duration-300",
            rec.type === 'danger' ? "bg-danger/5 border-danger/20" : 
            rec.type === 'warning' ? "bg-warning/5 border-warning/20" : 
            "bg-primary/5 border-primary/20"
          )}>
            <div className="flex items-start gap-3">
              <div className={clsx("p-1.5 rounded-lg shrink-0", 
                rec.type === 'danger' ? "bg-danger/10 text-danger" : 
                rec.type === 'warning' ? "bg-warning/10 text-warning" : 
                "bg-primary/10 text-primary"
              )}>
                <SafeIcon icon={rec.icon} size={16} />
              </div>
              <div className="space-y-1">
                <span className={clsx("text-[9px] font-black px-1.5 py-0.5 rounded uppercase", 
                  rec.type === 'danger' ? "bg-danger/20 text-danger" : 
                  rec.type === 'warning' ? "bg-warning/20 text-warning" : 
                  "bg-primary/20 text-primary"
                )}>
                  {rec.label}
                </span>
                <p className="text-xs font-medium text-text/90 leading-relaxed">{rec.text}</p>
              </div>
            </div>
          </div>
        )) : (
          <div className="p-4 text-center border border-dashed border-white/10 rounded-xl">
             <p className="text-[10px] text-muted font-bold uppercase tracking-widest">Awaiting active optimization signals...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationEngine;